import { LitElement, html, nothing } from 'lit';

export class CountdownTimer extends LitElement {
  static get properties() {
    return {
      end: { type: String },
      end_message: { type: String, attribute: 'end-message' },
      collapse_days: { type: Boolean, attribute: 'collapse-days' },
      redirect_url: { type: String, attribute: 'redirect-url' },
      countdown_color: { type: String, attribute: 'countdown-color' },
      _expired: { state: true },
      _d: { state: true },
      _h: { state: true },
      _m: { state: true },
      _s: { state: true },
    };
  }

  constructor() {
    super();
    this._daysEl = null;
    this._hoursEl = null;
    this._minutesEl = null;
    this._secondsEl = null;
    this._daysGroupEl = null;
    this._expired = false;
    this._d = 0;
    this._h = 0;
    this._m = 0;
    this._s = 0;
    this._interval = null;
  }

  createRenderRoot() {
    return this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => {
      this._handleSlotChange();
      this._start();
    });
  }

  disconnectedCallback() {
    if (this._interval) {
      clearInterval(this._interval);
    }
    super.disconnectedCallback();
  }


  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('_d') && this._daysEl) {
      this._setDigits(this._daysEl, this._pad(this._d), { isDays: true });
    }
    if (changedProperties.has('_h') && this._hoursEl) {
      this._setDigits(this._hoursEl, this._pad(this._h));
    }
    if (changedProperties.has('_m') && this._minutesEl) {
      this._setDigits(this._minutesEl, this._pad(this._m));
    }
    if (changedProperties.has('_s') && this._secondsEl) {
      this._setDigits(this._secondsEl, this._pad(this._s));
    }

    if (changedProperties.has('_d') && this._daysGroupEl) {
      const showDays = !(this.collapse_days && this._d === 0);
      this._daysGroupEl.classList.toggle('hidden', !showDays);
      this._daysGroupEl.classList.toggle('inline-flex', showDays);
    }
  }

  _handleSlotChange() {
    const slot = this.shadowRoot?.querySelector('slot[name="content"]');
    if (!slot) return;

    const content = slot.assignedElements({ flatten: true })[0];
    if (!content) return;

    this._daysEl = content.querySelector('[data-days]');
    this._hoursEl = content.querySelector('[data-hours]');
    this._minutesEl = content.querySelector('[data-minutes]');
    this._secondsEl = content.querySelector('[data-seconds]');
    this._daysGroupEl = content.querySelector('[data-days-group]');

    if (this.end) {
      const endMs = new Date(this.end).getTime();
      if (!isNaN(endMs)) {
        this._tick(endMs);
      }
    }

    if (this._daysGroupEl) {
      const showDays = !(this.collapse_days && this._d === 0);
      this._daysGroupEl.classList.toggle('hidden', !showDays);
      this._daysGroupEl.classList.toggle('inline-flex', showDays);
    }
  }

  _start() {
    const endMs = new Date(this.end).getTime();
    if (isNaN(endMs)) return;

    if (endMs - Date.now() <= 0) {
      this._expired = true;
      this.requestUpdate();
      if (this.redirect_url) {
        setTimeout(() => {
          window.location.href = this.redirect_url;
        }, this.end_message ? 3000 : 1000);
      }
      return;
    }

    this._interval = setInterval(() => this._tick(endMs), 1000);
  }

  _tick(endMs) {
    const target = endMs || new Date(this.end).getTime();
    const diff = target - Date.now();

    if (diff <= 0) {
      if (this._interval) {
        clearInterval(this._interval);
      }
      this._expired = true;
      this.requestUpdate(); 
      this.dispatchEvent(new CustomEvent('done', { bubbles: true, composed: true }));
      
      if (this.redirect_url) {
        setTimeout(() => {
          window.location.href = this.redirect_url;
        }, this.end_message ? 3000 : 1000); 
      }
      
      return;
    }

    const totalSec = Math.floor(diff / 1000);
    this._d = Math.floor(totalSec / 86400);
    this._h = Math.floor((totalSec % 86400) / 3600);
    this._m = Math.floor((totalSec % 3600) / 60);
    this._s = totalSec % 60;
  }

  _pad(n) {
    return String(n).padStart(2, '0');
  }

  _setDigits(el, value, { isDays = false } = {}) {
    if (!el) return;
    const str = String(value);

    // Days can exceed 2 digits; keep stable min-width but allow growth.
    if (isDays) {
      el.innerHTML = str
        .split('')
        .map(ch => `<span class="countdown_digit">${ch}</span>`)
        .join('');
      return;
    }

    // HH/MM/SS are always 2 chars because we pad with 0.
    const two = str.padStart(2, '0').slice(-2);
    el.innerHTML = two
      .split('')
      .map(ch => `<span class="countdown_digit">${ch}</span>`)
      .join('');
  }

  render() {
    if (this._expired) {
      return html`
        <div class="countdown_end_message">
          ${this.end_message
            ? html`<span>${this.end_message}</span>`
            : nothing}
        </div>
      `;
    }

    return html`
      <slot name="content" @slotchange=${this._handleSlotChange}></slot>
    `;
  }
}

if (!window.customElements.get('countdown-timer')) {
  window.customElements.define('countdown-timer', CountdownTimer);
}
